// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { formatDate } from '../../lib/formatLocale.js';
import { CURRENCIES, getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import { RATE_PRECISION } from '../../lib/currency-math.js';
import type { PriceAddedPayload, PriceUpdatedPayload, PriceDeletedPayload } from '../../types.js';

interface ExchangeRateManagerProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle';
const labelCls = 'block text-xs text-text-muted mb-1';

function formatRateDisplay(value: number, fromCode: string, toCode: string): string {
  const rate = value / RATE_PRECISION;
  const toDecimals = getCurrencyDecimalPlaces(toCode);
  return `1 ${fromCode} = ${rate.toFixed(Math.max(4, toDecimals + 4))} ${toCode}`;
}

function parseRate(input: string): number | null {
  const num = parseFloat(input);
  if (isNaN(num) || num <= 0) return null;
  return Math.round(num * RATE_PRECISION);
}

export function ExchangeRateManager({ worker, keyData }: ExchangeRateManagerProps) {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);
  const homeCurrency = ledger.settings.homeCurrency;

  const [showAdd, setShowAdd] = useState(false);
  const [addCurrency, setAddCurrency] = useState('EUR');
  const [addDate, setAddDate] = useState(new Date().toISOString().slice(0, 10));
  const [addRate, setAddRate] = useState('');
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRate, setEditRate] = useState('');
  const [editError, setEditError] = useState('');

  const csvRef = useRef<HTMLInputElement>(null);
  const [csvError, setCsvError] = useState('');

  // Only show exchange rate prices (where commodityId is a currency code)
  const allCurrencyCodes = useMemo(() => new Set(CURRENCIES.map((c) => c.code)), []);
  const exchangeRates = useMemo(() => {
    return [...ledger.prices.values()]
      .filter((p) => allCurrencyCodes.has(p.commodityId) && p.currency === homeCurrency && p.commodityId !== homeCurrency)
      .sort((a, b) => {
        if (a.commodityId !== b.commodityId) return a.commodityId.localeCompare(b.commodityId);
        return b.date.localeCompare(a.date);
      });
  }, [ledger.prices, allCurrencyCodes, homeCurrency]);

  const currencyOptions = useMemo(
    () => CURRENCIES.filter((c) => c.code !== homeCurrency).sort((a, b) => a.code.localeCompare(b.code)),
    [homeCurrency],
  );

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAddError('');
    const rateValue = parseRate(addRate);
    if (!rateValue) { setAddError(t('exchangeRates.invalidRate')); return; }
    setSaving(true);
    try {
      const payload: PriceAddedPayload = {
        priceId: uuidv4(),
        commodityId: addCurrency,
        currency: homeCurrency,
        date: addDate,
        source: 'user',
        type: 'last',
        value: rateValue,
      };
      await encryptAndPush(await buildEvent('PriceAdded', payload, ledger.lastSequence + 1));
      setShowAdd(false);
      setAddRate('');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t('exchangeRates.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleEditSave(priceId: string) {
    setEditError('');
    const rateValue = parseRate(editRate);
    if (!rateValue) { setEditError(t('exchangeRates.invalidRate')); return; }
    setSaving(true);
    try {
      const payload: PriceUpdatedPayload = { priceId, value: rateValue };
      await encryptAndPush(await buildEvent('PriceUpdated', payload, ledger.lastSequence + 1));
      setEditingId(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : t('exchangeRates.updateFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(priceId: string) {
    setSaving(true);
    try {
      const payload: PriceDeletedPayload = { priceId };
      await encryptAndPush(await buildEvent('PriceDeleted', payload, ledger.lastSequence + 1));
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t('exchangeRates.deleteFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    setCsvError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').filter((l) => l.trim());
    let seq = ledger.lastSequence;
    let imported = 0;
    for (const line of lines.slice(1)) { // skip header
      const parts = line.split(',').map((p) => p.trim());
      if (parts.length < 3) continue;
      const [dateStr, codeStr, rateStr] = parts;
      if (!dateStr || !codeStr || !rateStr) continue;
      const code = codeStr.toUpperCase();
      if (!allCurrencyCodes.has(code) || code === homeCurrency) continue;
      const rateValue = parseRate(rateStr);
      if (!rateValue) continue;
      seq++;
      const payload: PriceAddedPayload = {
        priceId: uuidv4(),
        commodityId: code,
        currency: homeCurrency,
        date: dateStr,
        source: 'user',
        type: 'last',
        value: rateValue,
      };
      await encryptAndPush(await buildEvent('PriceAdded', payload, seq));
      imported++;
    }
    if (imported === 0) setCsvError(t('exchangeRates.noValidRows'));
    if (csvRef.current) csvRef.current.value = '';
  }

  return (
    <div className="flex-1 overflow-auto p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-md font-semibold text-text">{t('exchangeRates.title')}</h2>
          <p className="text-xs text-text-muted mt-0.5">
            {t('exchangeRates.relativeTo')} <span className="font-mono text-text">{homeCurrency}</span>
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          {t('exchangeRates.addRate')}
        </button>
      </div>

      {/* Add Rate Dialog */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-surface border border-border max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-text">{t('exchangeRates.add')}</h3>
              <button onClick={() => { setShowAdd(false); setAddError(''); }} className="text-text-muted hover:text-text text-xl">&times;</button>
            </div>
            {addError && <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{addError}</div>}
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className={labelCls}>{t('exchangeRates.currency')}</label>
                <select
                  value={addCurrency}
                  onChange={(e) => setAddCurrency(e.target.value)}
                  className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
                >
                  {currencyOptions.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t('date')}</label>
                <input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t('exchangeRates.rateLabel', { from: addCurrency, to: homeCurrency })}</label>
                <input
                  type="number"
                  step="any"
                  value={addRate}
                  onChange={(e) => setAddRate(e.target.value)}
                  className={`${inputCls} font-mono`}
                  placeholder={t('exchangeRates.ratePlaceholder')}
                  autoFocus
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setShowAdd(false); setAddError(''); }}
                  className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
                  {t('common:actions.cancel')}
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors">
                  {saving ? t('saving') : t('exchangeRates.saveRate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rates Table */}
      {exchangeRates.length === 0 ? (
        <div className="bg-bg-surface border border-border p-8 text-center text-text-muted text-sm">
          {t('exchangeRates.empty')}
        </div>
      ) : (
        <div className="bg-bg-surface border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border-strong">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('exchangeRates.currency')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('date')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('exchangeRates.rate')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('exchangeRates.source')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {exchangeRates.map((price) => (
                <tr key={price.id} className="border-b border-border hover:bg-bg-elevated">
                  <td className="py-2 px-4 font-mono text-text font-medium">{price.commodityId}</td>
                  <td className="py-2 px-4 text-text-muted">
                    {formatDate(price.date, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td className="py-2 px-4 font-mono text-text text-xs">
                    {editingId === price.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-text-muted">1 {price.commodityId} =</span>
                        <input
                          type="number"
                          step="any"
                          value={editRate}
                          onChange={(e) => setEditRate(e.target.value)}
                          className="w-28 bg-bg border border-accent text-text text-sm px-2 py-1 focus:outline-none font-mono"
                          autoFocus
                        />
                        <span className="text-text-muted">{homeCurrency}</span>
                      </div>
                    ) : (
                      formatRateDisplay(price.value, price.commodityId, homeCurrency)
                    )}
                    {editingId === price.id && editError && (
                      <div className="text-negative text-xs mt-1">{editError}</div>
                    )}
                  </td>
                  <td className="py-2 px-4 text-text-muted text-xs">{price.source}</td>
                  <td className="py-2 px-4 text-right">
                    {editingId === price.id ? (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setEditingId(null); setEditError(''); }}
                          className="text-xs text-text-muted hover:text-text px-2 py-0.5">{t('common:actions.cancel')}</button>
                        <button onClick={() => handleEditSave(price.id)} disabled={saving}
                          className="text-xs text-accent hover:text-accent-hover px-2 py-0.5">{t('common:actions.save')}</button>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100">
                        <button onClick={() => { setEditingId(price.id); setEditRate(String(price.value / RATE_PRECISION)); setEditError(''); }}
                          className="text-xs text-text-muted hover:text-accent px-2 py-0.5">{t('common:actions.edit')}</button>
                        <button onClick={() => handleDelete(price.id)} disabled={saving}
                          className="text-xs text-text-muted hover:text-negative px-2 py-0.5">{t('common:actions.delete')}</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CSV Import */}
      <div className="mt-6 bg-bg-surface border border-border p-4">
        <h3 className="text-sm font-semibold text-text mb-1">{t('exchangeRates.importCsv')}</h3>
        <p className="text-xs text-text-muted mb-3">
          {t('exchangeRates.csvFormatLabel')} <span className="font-mono">date,currency,rate</span> {t('exchangeRates.headerRowRequired')}
        </p>
        {csvError && <div className="border border-negative text-negative px-3 py-2 text-sm mb-3">{csvError}</div>}
        <input
          ref={csvRef}
          type="file"
          accept=".csv"
          onChange={handleCsvImport}
          className="text-sm text-text-muted file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-bg-elevated file:text-text-muted hover:file:bg-bg-surface"
        />
      </div>
    </div>
  );
}
